import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BACKGROUNDS = [
  "Software Engineer",
  "Data Scientist / ML Engineer",
  "Data Analyst",
  "Product Manager",
  "Founder / Technical Lead",
  "Student / Recent Graduate",
  "Other",
];

const YEARS = ["0-1 years", "1-3 years", "3-5 years", "5-8 years", "8+ years"];

export default function Apply() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    linkedin: "",
    resume_link: "",
    background: "",
    years_experience: "",
    motivation: "",
    preferred_cohort: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.full_name ||
      !form.email ||
      !form.phone ||
      !form.background ||
      !form.years_experience ||
      form.motivation.length < 10
    ) {
      toast.error("Please complete every required field.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, form);
      setSubmitted(true);
      toast.success("Application received. We'll be in touch.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(Array.isArray(msg) ? "Please check your inputs." : msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] text-slate-900" data-testid="apply-success-page">
        <Nav />
        <main className="max-w-3xl mx-auto px-6 md:px-10 pt-40 pb-32 text-center">
          <p className="font-mono uppercase tracking-[0.24em] text-xs text-slate-500 mb-6">
            — Application received
          </p>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1] tracking-tight text-slate-900">
            Thank you, <em className="text-slate-500">{form.full_name.split(" ")[0]}</em>.
          </h1>
          <p className="mt-10 text-lg text-slate-600 font-sans leading-relaxed max-w-xl mx-auto">
            Your application has been recorded. Our team reviews every submission
            individually — we&rsquo;ll reach out to you at&nbsp;
            <span className="text-slate-900 font-medium">{form.email}</span>
            &nbsp;with next steps.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/"
              className="border border-slate-300 hover:border-slate-900 px-8 py-4 font-sans text-sm tracking-wide"
              data-testid="apply-success-home-btn"
            >
              ← Back to the program
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-900" data-testid="apply-page">
      <Nav />

      <main className="max-w-3xl mx-auto px-6 md:px-10 pt-36 pb-24">
        <p className="font-mono uppercase tracking-[0.24em] text-xs text-slate-500 mb-6">
          — Cohort application
        </p>
        <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tighter text-slate-900">
          Apply.
        </h1>
        <p className="mt-6 text-lg text-slate-600 font-sans max-w-xl leading-relaxed">
          The FDE AI Industry Practitioner Program admits a small cohort per
          intake. Tell us about your background and what you want to ship.
        </p>

        <form
          onSubmit={handleSubmit}
          className="mt-16 space-y-10"
          data-testid="apply-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Field label="Full name" required>
              <Input
                data-testid="full-name-input"
                value={form.full_name}
                onChange={set("full_name")}
                placeholder="Your name"
                className="rounded-none border-slate-300 focus-visible:ring-slate-900 focus-visible:ring-1 h-12"
                required
              />
            </Field>
            <Field label="Email" required>
              <Input
                data-testid="email-input"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@domain.com"
                className="rounded-none border-slate-300 focus-visible:ring-slate-900 focus-visible:ring-1 h-12"
                required
              />
            </Field>
            <Field label="Phone" required>
              <Input
                data-testid="phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="+91 · 555 · 0100"
                className="rounded-none border-slate-300 focus-visible:ring-slate-900 focus-visible:ring-1 h-12"
                required
              />
            </Field>
            <Field label="LinkedIn (optional)">
              <Input
                data-testid="linkedin-input"
                value={form.linkedin}
                onChange={set("linkedin")}
                placeholder="linkedin.com/in/…"
                className="rounded-none border-slate-300 focus-visible:ring-slate-900 focus-visible:ring-1 h-12"
              />
            </Field>
            <Field label="Background" required>
              <Select
                value={form.background}
                onValueChange={(v) => setForm((f) => ({ ...f, background: v }))}
              >
                <SelectTrigger
                  data-testid="background-select"
                  className="rounded-none border-slate-300 h-12 focus:ring-slate-900 focus:ring-1"
                >
                  <SelectValue placeholder="Select your background" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {BACKGROUNDS.map((b) => (
                    <SelectItem key={b} value={b}>
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Years of experience" required>
              <Select
                value={form.years_experience}
                onValueChange={(v) => setForm((f) => ({ ...f, years_experience: v }))}
              >
                <SelectTrigger
                  data-testid="years-select"
                  className="rounded-none border-slate-300 h-12 focus:ring-slate-900 focus:ring-1"
                >
                  <SelectValue placeholder="Select experience" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {YEARS.map((y) => (
                    <SelectItem key={y} value={y}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Resume / portfolio link (optional)">
            <Input
              data-testid="resume-link-input"
              value={form.resume_link}
              onChange={set("resume_link")}
              placeholder="https://…"
              className="rounded-none border-slate-300 focus-visible:ring-slate-900 focus-visible:ring-1 h-12"
            />
          </Field>

          <Field label="Preferred cohort (optional)">
            <Input
              data-testid="cohort-input"
              value={form.preferred_cohort}
              onChange={set("preferred_cohort")}
              placeholder="Any timing preference or start month"
              className="rounded-none border-slate-300 focus-visible:ring-slate-900 focus-visible:ring-1 h-12"
            />
          </Field>

          <Field
            label="Why this program?"
            hint="What do you want to ship by the end of it? (min 10 characters)"
            required
          >
            <Textarea
              data-testid="motivation-textarea"
              value={form.motivation}
              onChange={set("motivation")}
              placeholder="Tell us what you want to build — a copilot, an agent system, a client-ready RAG pipeline, a monitored ML API…"
              className="rounded-none border-slate-300 focus-visible:ring-slate-900 focus-visible:ring-1 min-h-[160px]"
              required
            />
          </Field>

          <div className="pt-6 flex flex-col sm:flex-row gap-4 border-t border-slate-200">
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-application-btn"
              className="bg-slate-900 text-white px-10 py-5 font-sans tracking-wide hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? "Submitting…" : "Submit application"}
            </button>
            <Link
              to="/"
              className="text-slate-600 hover:text-slate-900 px-6 py-5 font-sans text-sm self-center"
            >
              ← Back to program
            </Link>
          </div>
        </form>
      </main>

      <Footer />
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div className="space-y-3">
      <Label className="flex items-baseline gap-2 font-mono uppercase text-[10px] tracking-[0.24em] text-slate-600">
        {label}
        {required && <span className="text-slate-400 normal-case tracking-normal">*</span>}
        {hint && (
          <span className="normal-case tracking-normal text-slate-400 font-sans text-[11px]">
            — {hint}
          </span>
        )}
      </Label>
      {children}
    </div>
  );
}
